import { ApiListItem, VersionInfo, WorkflowData, WorkflowEvent } from "../../model";
import { OData, ODataResult, ODataQueryOptions } from "../../util";
import { Ajax } from "./Ajax";

/**
 * Base class for CRUD services.
 * Type TItem is the type of the object that is stored in the service (CrudObject).
 * Type TListItem is the type of the object that is returned in lists (based on Api).
 */
export abstract class CrudApiBase<TItem, TListItem extends ApiListItem> {

  /**
   * Deriving classes should define the baseURl for the method. E.g. "/calculations"
   * Ajax implementations will add the beginning (e.g. "https://test-api.salaxy.com/api/v03")
   */
  protected abstract baseUrl: string;

  /** Constructor creates a new CRUD api with given Ajax-implementation */
  constructor(protected ajax: Ajax) { }

  /**
   * Client-side (synchronous) method for getting a new blank CRUD item as bases for UI binding.
   * The basic idea is that all the child object properties should be non-null to avoid null reference exceptions.
   * In special cases, when null is significant it may be used: e.g. Calculation.result is null untill the recalculation has been done.
   * Strings, dates and booleans may be null (false if bool), but typically just undefined.
   */
  public abstract getBlank(): TItem;

  /**
   * Makes an OData query to the service's main OData method.
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public getOData(query: ODataQueryOptions | string): Promise<ODataResult<TListItem>> {
    const baseUrl = this.getODataUrl();
    return OData.getOData<TListItem>(baseUrl, query, this.ajax);
  }

  /**
   * Makes an OData query to the the given service URL.
   * @param baseUrl The service URL that is used, e.g. "/v03/api/taxcards/current".
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   * @deprecated You should use the OData.getOData() static util method instead.
   */
  public getODataBase(baseUrl: string, query: ODataQueryOptions | string): Promise<ODataResult<TListItem>> {
    // TODO: Check that it is not used and remove.
    return OData.getOData<TListItem>(baseUrl, query, this.ajax);
  }

  /**
   * Retrieves the Ajax instance used by this CRUD API.
   * @returns {Ajax} The Ajax instance that is used for making HTTP requests.
   */
  public getAjax(): Ajax {
    return this.ajax;
  }

  /**
   * Gets a single item based on identier
   * @param id - Unique identifier for the object
   * @returns A Promise with result data.
   */
  public getSingle(id: string): Promise<TItem> {
    return this.ajax.getJSON(`${this.baseUrl}/${id}`);
  }

  /**
   * Deletes an single item from the sotrage
   * @param id - Unique identifier for the object
   * @returns A Promise with result data "Object deleted".
   */
  public delete(id: string): Promise<string> {
    return this.ajax.remove(`${this.baseUrl}/${id}`);
  }

  /**
   * Saves an item to the storage.
   * If id is null, this is add/insert. If id exists, this is update.
   * @param itemToSave - The item that is updated/inserted.
   * @param options - Additional options for the save operation. Can be any object with fields that are used as query parameters.
   * @returns A Promise with result data as saved to the storage (contains id, createAt, owner etc.).
   */
  public save(itemToSave: TItem, options: any = undefined): Promise<TItem> {
    return this.ajax.postJSON(this.getUrlWithOptions(this.baseUrl, options), itemToSave);
  }

  /** Gets the OData URL for v03 compatible services */
  public getODataUrl(): string {
    if (this.baseUrl.startsWith("/v03") || this.baseUrl.startsWith("/v04")){
      // e.g. "/v03/api/messages"
      return this.baseUrl;
    }
    if (this.baseUrl.startsWith("http")) {
      return this.baseUrl;
    }
    throw new Error(`Service ${this.baseUrl} does not support OData interface.`);
  }

  /** Returns the base url for this api. */
  public getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Adds/updates the workflow event for the item.
   *
   * @param item - Item for which to set the workflow event.
   * @param wfEvent - Event to add or update.
   * @returns Workflow data for the item.
   */
  public saveWorkflowEvent(item: TItem, wfEvent: WorkflowEvent): Promise<WorkflowData> {
    const id = (item as any as { /** required id */ id: string }).id;
    return this.ajax.postJSON(`${this.baseUrl}/${id}/workflows`, wfEvent);
  }

  /**
   * Deletes the workflow item or items from the item.
   * @param item - Item for which to delete workflow events.
   * @param wfIdOrType - Event id or type.
   * @returns A Promise with result data "Object deleted".
   */
  public deleteWorkflowEvent(item: TItem, wfIdOrType: string): Promise<string> {
    const id = (item as any as { /** required id */ id: string }).id;
    return this.ajax.remove(`${this.baseUrl}/${id}/workflows/${wfIdOrType}`);
  }

  /**
   * Gets a single workflow event for the item.
   * @param item - Item for which to get the single event.
   * @param wfId - Event id.
   * @returns A Promise with event data.
   */
  public getWorkflowEvent(item: TItem, wfId: string): Promise<WorkflowEvent> {
    const id = (item as any as { /** required id */ id: string }).id;
    return this.ajax.getJSON(`${this.baseUrl}/${id}/workflows/events/${wfId}`);
  }

  /**
   * Lists versions for the object.
   * @param id - Unique identifier for the object
   * @returns A Promise with result data.
   */
  public getVersions(id: string): Promise<VersionInfo[]> {
    return this.ajax.getJSON(`${this.baseUrl}/${id}/versions`);
  }

  /**
   * Get the given version for the object.
   * @param id - Unique identifier for the object
   * @param versionId - Version identifier
   * @returns A Promise with result data.
   */
  public getVersion(id: string, versionId: string): Promise<TItem> {
    return this.ajax.getJSON(`${this.baseUrl}/${id}/versions/${versionId}`);
  }

  protected getUrlWithOptions(url: string, options: any): string {
    const prmsArr: string[] = [];
    options ??= {};
    Object.keys(options).forEach(key => {
      const val = options[key];
      if (val !== undefined && val !== null) {
        prmsArr.push(`${key}=${encodeURIComponent(val)}`);
      }
    });
    const prms = prmsArr.join("&");
    return `${url}${prms ? "?"+prms : ""}`;
  }
}
